import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, TROUBLE_CATEGORY_MISSING,
} from '../GuideKit';

/* Operations → Client Information Change → Investment Objective / Risk Update.
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Ops
 * (version active 2026-10-04): screen "Investment Objective / Risk Update
 * Screen" (region "Investment Objective / Risk Update Details") - one screen
 * holding the questions, upload tiles, notice and Acknowledgement. The other
 * two Client Information Change sub-categories use different screens and have
 * their own guides. Shared chrome and policy copy live in GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Operations',
    category: 'Client Information Change',
    subCategory: 'Investment Objective / Risk Update',
    subject: subjectRow('Risk score update - 0989-022356'),
    lookups: [['Household', 'The client household the update is for. The advisor and office are taken from it.']],
    note: (
      <>
        Address / Contact Update and Banking / ACH Instructions Update are different forms with their own
        guides - this one covers Investment Objective / Risk Update only.
      </>
    ),
  }),
  {
    title: 'Answer the IPS question and enter both bucket scores',
    body: (
      <p style={{ margin: 0 }}>
        Everything is on one screen, <strong>Investment Objective / Risk Update Details</strong>. Say whether you
        have a signed new or updated IPS, then enter the client&rsquo;s Soon and Later bucket scores. All three are
        required.
      </p>
    ),
  },
  {
    title: 'Write the instructions and attach documents',
    body: (
      <p style={{ margin: 0 }}>
        Fill in <em>Additional Instructions</em> (up to 2,000 characters). It becomes the case description
        Operations reads first, so say which accounts are affected and what should change. The upload tiles and
        the notice only appear once this box has something in it. Add the signed IPS or IPS Addendum and any other
        supporting documents to the tiles - the screen warns that <em>missing or incomplete documents may result
        in a NIGO.</em>
      </p>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice under the tiles, select <em>I have read, understand, and agree to
        the above information</em> under Acknowledgement and click Next. FSC creates the case, attaches your files
        and opens it. Note the case number for follow-up.
      </p>
    ),
  },
];

// Every question on the Investment Objective / Risk Update screen, in the order it can appear.
const QUESTIONS = [
  {
    screen: 'Investment Objective / Risk Update Details',
    q: 'Do you have a signed new/updated IPS or IPS Addendum?',
    when: 'Always - this is the first question',
    options: ['Yes (I have a new/updated IPS)', 'No (I do not have a new/updated IPS)'],
    note: 'The form lets you continue either way, and the answer is recorded on the case. If you have it, upload it to the tiles further down.',
  },
  {
    q: 'Soon Bucket Score',
    when: 'Always',
    note: 'A number between 7 and 28.',
  },
  {
    q: 'Later Bucket Score',
    when: 'Always',
    note: 'A number between 10 and 46.',
  },
  {
    q: 'Additional Instructions',
    when: 'Always',
    note: 'Required, 2,000 characters max. Becomes the case description. The upload tiles, notice and Acknowledgement stay hidden until this has text.',
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Operations', 'Client Information Change'),
  {
    h: "The upload tiles and Acknowledgement haven't appeared",
    p: ['They stay hidden until the Additional Instructions box has something in it.'],
  },
];

export default function ClientInfoInvestmentObjective() {
  return (
    <GuidePage
      title="Investment Objective and Risk Updates"
      icon="speedometer2"
      path="Operations → Client Information Change → Investment Objective / Risk Update"
      minutes={3}
      intro="How to send a client's updated investment objective or risk scores to the Prosperity Operations team - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. The form is short, but every field on it is required.</Para>
        <Bullets items={[
          <><strong>The client household</strong> the update is for.</>,
          <><strong>The Soon Bucket Score</strong> (7 to 28) and <strong>the Later Bucket Score</strong> (10 to 46).</>,
          <><strong>The signed new or updated IPS or IPS Addendum</strong>, if you have one, ready to upload.</>,
          <><strong>What should change</strong> and on which accounts, for the instructions box.</>,
        ]} />
        <Callout>
          Missing or incomplete documents may result in a NIGO, so have the signed IPS or Addendum in hand before
          you start if the change depends on it.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare before you sit down with a client, or to work out why a question you expected has not shown up.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext team="operations" />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It does not describe how the bucket scores are worked out or
        the suitability review of the new objective.
      </Footnote>
    </GuidePage>
  );
}
